import { apiClient } from '@/auth';

/**
 * Care-team patient notes. Each POST appends a row; GET list returns
 * newest first. The deprecated clinician GET/PUT shim
 * (`/patient/{id}/clinician/{id}/patient_note`) is left here so older
 * callers still compile, but the UI uses list + create.
 */

export type PatientNoteResponse = {
  id?: number;
  patient_id: number;
  /** server returns this as a string (the user id), not a number */
  created_by_user_id?: string;
  note: string;
  created_time?: string;
  updated_time?: string;
};

export const patientNotesApi = {
  async list(patientId: number): Promise<PatientNoteResponse[]> {
    const res = await apiClient.get<PatientNoteResponse[]>(
      `/patient/${patientId}/patient_notes`,
    );
    return res.data ?? [];
  },

  async create(patientId: number, note: string): Promise<PatientNoteResponse> {
    const res = await apiClient.post<PatientNoteResponse>(`/patient/${patientId}/patient_note`, {
      note,
    });
    return res.data;
  },

  async update(
    patientId: number,
    noteId: number,
    note: string,
  ): Promise<PatientNoteResponse> {
    const res = await apiClient.put<PatientNoteResponse>(
      `/patient/${patientId}/patient_note/${noteId}`,
      { note },
    );
    return res.data;
  },

  /**
   * @deprecated Latest-note shim. Prefer `list`.
   */
  async getForClinician(args: {
    patientId: number;
    clinicianId: number;
  }): Promise<PatientNoteResponse | null> {
    try {
      const res = await apiClient.get<PatientNoteResponse>(
        `/patient/${args.patientId}/clinician/${args.clinicianId}/patient_note`,
      );
      return res.data ?? null;
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      if (status === 404) return null;
      throw err;
    }
  },

  /**
   * @deprecated Overwrites the latest note. Prefer `create`.
   */
  async putForClinician(args: {
    patientId: number;
    clinicianId: number;
    note: string;
  }): Promise<PatientNoteResponse> {
    const res = await apiClient.put<PatientNoteResponse>(
      `/patient/${args.patientId}/clinician/${args.clinicianId}/patient_note`,
      { note: args.note },
    );
    return res.data;
  },
};
